import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import {
  DeepCard,
  FAQBlock,
  LinkPills,
  PillList,
  SectionIntro,
} from "../components/ContentBlocks";
import {
  eventCategories,
  eventCategoryGuides,
  eventFaqs,
  eventMatchesSubpage,
  getEventsBySubpage,
  pastRoboticsEvents,
  regionGuides,
  roboticsEventSubpages,
} from "../data/roboticsEvents";
import { seoConfig } from "../data/seoConfig";
import { getRenderDate } from "../utils/renderClock";
import EventSubmissionModal from "../components/community/EventSubmissionModal";

const PAGE_SIZE = 12;

const filterDefaults = {
  category: "All categories",
  country: "All countries",
  region: "All regions",
  month: "All months",
  year: "All years",
  format: "All formats",
  status: "All statuses",
};

const managedQueryKeys = [
  "q",
  "focus",
  "category",
  "country",
  "region",
  "month",
  "year",
  "format",
  "status",
  "view",
];

const monthOptions = [
  ["1", "January"],
  ["2", "February"],
  ["3", "March"],
  ["4", "April"],
  ["5", "May"],
  ["6", "June"],
  ["7", "July"],
  ["8", "August"],
  ["9", "September"],
  ["10", "October"],
  ["11", "November"],
  ["12", "December"],
];

const regionOptions = [
  "Europe", "China", "Japan", "United States", "United Arab Emirates",
  "Asia Pacific", "North America", "Middle East", "Africa", "Global online events",
];

function matchesRegion(event, region) {
  if (region === filterDefaults.region) return true;
  if (region === "United Arab Emirates") return event.country === region;
  if (region === "Asia Pacific") {
    return ["Asia Pacific", "China", "Japan", "Singapore", "South Korea"].includes(event.region) ||
      ["India", "Taiwan", "Thailand", "Malaysia", "Vietnam", "Indonesia", "Australia"].includes(event.country);
  }
  if (region === "North America") {
    return ["North America", "United States"].includes(event.region);
  }
  return event.region === region;
}

const quickFilters = [
  { value: "all", label: "All Events" },
  { value: "upcoming", label: "Upcoming" },
  { value: "now", label: "Happening Now" },
  { value: "past", label: "Past Events" },
  { value: "humanoid", label: "Humanoid Robotics" },
  { value: "industrial", label: "Industrial Robotics" },
  { value: "physical-ai", label: "Physical AI" },
  { value: "ai", label: "Artificial Intelligence" },
  { value: "conferences", label: "Conferences" },
  { value: "exhibitions", label: "Exhibitions" },
  { value: "competitions", label: "Competitions" },
  { value: "workshops", label: "Workshops" },
  { value: "online", label: "Online Events" },
];

const internalLinks = [
  { label: "Humanoid Robots", to: "/robot-types/humanoid-robots" },
  { label: "Industrial Robots", to: "/robot-types/industrial-robots" },
  { label: "Robot Sensors", to: "/robot-sensors" },
  { label: "Robotics Software", to: "/robotics-software" },
  { label: "Robotics Companies", to: "/robotics-companies" },
  { label: "Physical AI", to: "/robotics-topics" },
];

const genericEventCopy = new Set([
  "This confirmed event provides a practical view of current robotics research, commercial systems, suppliers, deployment constraints and emerging applications.",
  "Check official registration page.",
  "Check the official event page for registration, exhibition and program deadlines.",
  "Check the official event page.",
  "Check the official exhibitor page.",
  "Check the official call or program page.",
]);

function isSpecificEventText(value) {
  const text = String(value || "").trim();
  if (!text || genericEventCopy.has(text)) return false;
  if (
    /^High for international visitors traveling to .+; use the organizer’s current registration and invitation-document process\.$/.test(
      text,
    )
  ) {
    return false;
  }
  return true;
}

function getOfficialEventLinks(event) {
  const links = [
    ...(event.website
      ? [{ label: "Official website", url: event.website }]
      : []),
    ...(event.officialLinks || []),
  ];
  const seen = new Set();
  return links.filter((item) => {
    const url = String(item?.url || "").trim();
    if (!url || seen.has(url)) return false;
    seen.add(url);
    return true;
  });
}

function getPlanningItems(event) {
  return [
    { label: "Tickets / price", value: event.priceRange },
    { label: "Registration / deadline", value: event.deadline },
    { label: "Travel / visa", value: event.visaRelevance },
  ].filter((item) => isSpecificEventText(item.value));
}

function parseLocalDate(value) {
  if (!value || typeof value !== "string") return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfToday() {
  const date = getRenderDate();
  date.setHours(0, 0, 0, 0);
  return date;
}

function endOfDay(date) {
  const value = new Date(date);
  value.setHours(23, 59, 59, 999);
  return value;
}

function formatDateRange(event) {
  const start = parseLocalDate(event.dateStart);
  const end = parseLocalDate(event.dateEnd);
  if (!start) return event.date || "Date to be confirmed";

  const fullDate = new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  if (!end || event.dateStart === event.dateEnd) return fullDate.format(start);

  const sameMonth =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear();

  if (sameMonth) {
    return `${new Intl.DateTimeFormat("en", { month: "short" }).format(start)} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
  }

  return `${fullDate.format(start)} – ${fullDate.format(end)}`;
}

function eventLocation(event) {
  const city = String(event.city || "").trim();
  const country = String(event.country || "").trim();
  if (city && country) return `${city}, ${country}`;
  return city || country || "Location not announced";
}

function getEventFormat(event) {
  const source = [
    event.eventType,
    event.region,
    event.city,
    event.venue,
    event.description,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (source.includes("hybrid")) return "Hybrid";
  if (
    source.includes("online") ||
    source.includes("virtual") ||
    event.region === "Global online events"
  ) {
    return "Online";
  }
  if (event.city || event.venue) return "In Person";
  return "Format not stated";
}

function getTemporalStatus(event) {
  const rawStatus = String(event.status || "").toLowerCase();
  if (rawStatus.includes("cancel")) return "Cancelled";
  if (rawStatus.includes("postpon")) return "Postponed";

  const today = startOfToday();
  const start = parseLocalDate(event.dateStart);
  const end = parseLocalDate(event.dateEnd || event.dateStart);

  if (!start && event.announcedYear) {
    const lastPossibleDay = new Date(Number(event.announcedYear), Number(event.announcedMonth || 12), 0);
    if (endOfDay(lastPossibleDay) < today) return "Finished";
    return "Dates to be confirmed";
  }

  if (end && endOfDay(end) < today) return "Finished";
  if (start && end && start <= today && endOfDay(end) >= today) {
    return "Happening Now";
  }
  if (rawStatus.includes("registration closed")) return "Registration Closed";
  if (rawStatus.includes("registration open")) return "Registration Open";
  if (rawStatus.includes("completed") || rawStatus.includes("past event")) {
    return "Finished";
  }
  return "Upcoming";
}

function statusClass(status) {
  if (status === "Happening Now") {
    return "border-emerald-200 bg-emerald-50 text-emerald-800";
  }
  if (status === "Registration Open") {
    return "border-blue-200 bg-blue-50 text-blue-800";
  }
  if (status === "Registration Closed") {
    return "border-amber-200 bg-amber-50 text-amber-800";
  }
  if (status === "Cancelled" || status === "Postponed") {
    return "border-red-200 bg-red-50 text-red-800";
  }
  if (status === "Finished") {
    return "border-neutral-200 bg-neutral-100 text-neutral-700";
  }
  return "border-violet-200 bg-violet-50 text-violet-800";
}

function isPastEvent(event) {
  return getTemporalStatus(event) === "Finished";
}

function sortEvents(events, descending = false) {
  return [...events].sort((left, right) => {
    const leftTime = parseLocalDate(left.dateStart)?.getTime();
    const rightTime = parseLocalDate(right.dateStart)?.getTime();

    if (leftTime == null && rightTime == null) {
      return String(left.name || "").localeCompare(String(right.name || ""));
    }
    if (leftTime == null) return 1;
    if (rightTime == null) return -1;
    return descending ? rightTime - leftTime : leftTime - rightTime;
  });
}

function archiveFromEvent(event) {
  return {
    ...event,
    archiveText: event.description || event.whyItMatters,
  };
}

function normalizeArchiveEvent(event) {
  return {
    ...event,
    dateStart: event.dateStart || null,
    dateEnd: event.dateEnd || null,
    description: event.description || event.reasonToArchive || "Archive record",
    archiveText: event.reasonToArchive || event.description || "Archive record",
    status: event.status || "Past event",
  };
}

function dedupeEvents(events) {
  const seen = new Set();
  return events.filter((event) => {
    const key = `${String(event.name || "").toLowerCase()}|${event.dateStart || event.date || ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getEventYear(event) {
  const start = parseLocalDate(event.dateStart);
  if (start) return String(start.getFullYear());
  if (event.announcedYear) return String(event.announcedYear);
  const match = String(event.date || "").match(/\b(20\d{2})\b/);
  return match ? match[1] : "";
}

function getEventMonth(event) {
  const start = parseLocalDate(event.dateStart);
  return String(start ? start.getMonth() + 1 : event.announcedMonth || "");
}

function searchableEventText(event) {
  return [
    event.name,
    event.category,
    event.country,
    event.city,
    event.region,
    event.month,
    event.venue,
    event.description,
    event.whyItMatters,
    event.source,
    event.eventType,
    event.status,
    event.priceRange,
    event.deadline,
    ...(event.mainTopics || []),
    ...(event.bestFor || []),
    ...(event.audience || []),
    ...(event.organizers || []),
    ...(event.coOrganizers || []),
    ...(event.verifiedFacts || []),
    ...(event.featuredRobots || []),
    ...(event.technologyHighlights || []),
    ...(event.officialLinks || []).flatMap((item) => [item.label, item.url]),
    ...(event.seoKeywords || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function matchesFocus(event, focus) {
  const status = getTemporalStatus(event);
  const text = [event.name, event.eventType, event.description, ...(event.mainTopics || [])]
    .filter(Boolean).join(" ").toLowerCase();
  const format = getEventFormat(event);

  switch (focus) {
    case "upcoming":
      return ["Upcoming", "Registration Open", "Registration Closed", "Dates to be confirmed"].includes(
        status,
      );
    case "now":
      return status === "Happening Now";
    case "humanoid":
      return event.category === "Humanoid Robots" || text.includes("humanoid");
    case "industrial":
      return [
        "Industrial Automation",
        "Smart Manufacturing",
        "Robot Components",
      ].includes(event.category);
    case "physical-ai":
      return (
        event.category === "Physical AI" ||
        text.includes("physical ai") ||
        text.includes("embodied ai") ||
        text.includes("embodied intelligence")
      );
    case "ai":
      return (
        event.category === "AI Robotics" ||
        text.includes("artificial intelligence")
      );
    case "conferences":
      return (
        event.category === "Research Conferences" ||
        text.includes("conference") ||
        text.includes("symposium")
      );
    case "exhibitions":
      return (
        [
          "Industrial Automation",
          "Robot Components",
          "Warehouse Robotics",
          "Consumer Robotics",
        ].includes(event.category) || /expo|exhibition|trade show/.test(text)
      );
    case "competitions":
      return (
        event.category === "Robotics Competitions" ||
        /competition|challenge|robocup/.test(text)
      );
    case "workshops":
      return text.includes("workshop");
    case "online":
      return format === "Online";
    default:
      return true;
  }
}

function getDateGroup(event) {
  const start = parseLocalDate(event.dateStart);
  if (!start) return "Date to be confirmed";

  const today = startOfToday();
  const thisMonth = today.getMonth();
  const thisYear = today.getFullYear();
  const nextMonthDate = new Date(thisYear, thisMonth + 1, 1);

  if (start.getFullYear() === thisYear && start.getMonth() === thisMonth) {
    return "This Month";
  }
  if (
    start.getFullYear() === nextMonthDate.getFullYear() &&
    start.getMonth() === nextMonthDate.getMonth()
  ) {
    return "Next Month";
  }
  if (start.getFullYear() === thisYear) return "Later This Year";
  if (start.getFullYear() === thisYear + 1) return "Next Year";
  return start.getFullYear() > thisYear + 1 ? "Future Events" : "Past Events";
}

function groupEvents(events, forcePast = false) {
  const order = [
    "This Month",
    "Next Month",
    "Later This Year",
    "Next Year",
    "Future Events",
    "Date to be confirmed",
    "Past Events",
  ];

  const groups = events.reduce((result, event) => {
    const key = forcePast ? "Past Events" : getDateGroup(event);
    if (!result[key]) result[key] = [];
    result[key].push(event);
    return result;
  }, {});

  return order
    .filter((key) => groups[key]?.length)
    .map((key) => [key, groups[key]]);
}

function buildCalendarMonths(events) {
  const groups = events.reduce((result, event) => {
    const start = parseLocalDate(event.dateStart);
    if (!start) return result;
    const key = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`;
    if (!result[key]) result[key] = [];
    result[key].push(event);
    return result;
  }, {});

  return Object.entries(groups)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, monthEvents]) => {
      const [year, month] = key.split("-").map(Number);
      return {
        key,
        year,
        month,
        label: new Intl.DateTimeFormat("en", {
          month: "long",
          year: "numeric",
        }).format(new Date(year, month - 1, 1)),
        events: monthEvents,
      };
    });
}

function DateTile({ event }) {
  const start = parseLocalDate(event.dateStart);
  if (!start) {
    return (
      <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-2xl border border-neutral-200 bg-neutral-50 text-center">
        <span className="text-[10px] font-black uppercase tracking-[0.16em] text-neutral-500">
          Date
        </span>
        <span className="mt-1 text-sm font-black text-neutral-950">{getTemporalStatus(event) === "Finished" ? "Archive" : "TBC"}</span>
      </div>
    );
  }

  return (
    <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-2xl border border-blue-100 bg-blue-50 text-center">
      <span className="text-[10px] font-black uppercase tracking-[0.16em] text-blue-700">
        {new Intl.DateTimeFormat("en", { month: "short" }).format(start)}
      </span>
      <span className="mt-0.5 text-2xl font-black leading-none text-neutral-950">
        {start.getDate()}
      </span>
      <span className="mt-1 text-[11px] font-bold text-neutral-500">
        {start.getFullYear()}
      </span>
    </div>
  );
}

function EventCard({ event, headingLevel = 3 }) {
  const Heading = `h${headingLevel}`;
  const status = getTemporalStatus(event);
  const format = getEventFormat(event);
  const description =
    event.description || event.archiveText || "Event details are unavailable.";
  const whyFollow = isSpecificEventText(event.whyItMatters)
    ? event.whyItMatters
    : "";
  const planningItems = getPlanningItems(event);
  const officialLinks = getOfficialEventLinks(event);

  return (
    <article className="soft-card soft-card-hover group p-4 sm:p-5">
      <div className="flex items-start gap-4">
        <DateTile event={event} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-black ${statusClass(status)}`}
            >
              {status}
            </span>
            <span className="data-badge">{format}</span>
          </div>
          <Heading className="mt-3 text-xl font-black tracking-tight text-neutral-950 sm:text-2xl">
            {event.website ? (
              <a
                href={event.website}
                target="_blank"
                rel="noreferrer"
                className="focus-ring rounded-md decoration-2 underline-offset-4 hover:text-blue-700 hover:underline"
              >
                {event.name}
              </a>
            ) : (
              event.name
            )}
          </Heading>
          <p className="mt-2 text-sm font-bold text-neutral-700">
            {eventLocation(event)}
          </p>
          <p className="mt-1 text-sm text-neutral-500">
            {formatDateRange(event)}
          </p>
        </div>
      </div>

      <p className="mt-4 text-sm leading-7 text-neutral-600">
        {description}
      </p>

      {event.dateNote && (
        <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-950">
          {event.dateNote}
        </p>
      )}

      {event.website && event.source && (
        <p className="mt-2 text-xs leading-6 text-neutral-500">
          Source {" "}
          <a href={event.website} target="_blank" rel="noreferrer" className="focus-ring rounded text-blue-700 underline underline-offset-2">
            {event.source}
          </a>
        </p>
      )}

      {whyFollow && (
        <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/70 p-4">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-blue-800">
            Why follow this event
          </p>
          <p className="mt-2 text-sm leading-6 text-neutral-700">{whyFollow}</p>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <span className="data-badge">
          {event.category || "Category not stated"}
        </span>
        {(event.mainTopics || []).slice(0, 2).map((topic) => (
          <span key={topic} className="data-badge">
            {topic}
          </span>
        ))}
      </div>

      {Boolean(event.originalName ||
        event.organizers?.length ||
        event.coOrganizers?.length ||
        event.verifiedFacts?.length ||
        event.featuredRobots?.length ||
        event.technologyHighlights?.length ||
        event.bestFor?.length ||
        planningItems.length ||
        event.officialLinks?.length) && (
        <details className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-4">
          <summary className="focus-ring cursor-pointer rounded-md text-sm font-black text-neutral-900">
            Event details and official sources
          </summary>
          <div className="mt-4 grid gap-4 text-sm leading-6 text-neutral-700">
            {event.originalName && (
              <div>
                <p className="font-black text-neutral-950">Original name</p>
                <p className="mt-1">{event.originalName}</p>
              </div>
            )}
            {event.organizers?.length > 0 && (
              <div>
                <p className="font-black text-neutral-950">Organizer</p>
                <p className="mt-1">{event.organizers.join(", ")}</p>
              </div>
            )}
            {event.coOrganizers?.length > 0 && (
              <div>
                <p className="font-black text-neutral-950">Co-organizer</p>
                <p className="mt-1">{event.coOrganizers.join(", ")}</p>
              </div>
            )}
            {event.bestFor?.length > 0 && (
              <div>
                <p className="font-black text-neutral-950">Useful for</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {event.bestFor.map((item) => (
                    <span key={item} className="data-badge">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {event.verifiedFacts?.length > 0 && (
              <div>
                <p className="font-black text-neutral-950">Confirmed facts</p>
                <ul className="mt-2 grid gap-2 pl-5 list-disc">
                  {event.verifiedFacts.map((fact) => (
                    <li key={fact}>{fact}</li>
                  ))}
                </ul>
              </div>
            )}
            {event.featuredRobots?.length > 0 && (
              <div>
                <p className="font-black text-neutral-950">Robots and systems</p>
                <ul className="mt-2 grid gap-2 pl-5 list-disc">
                  {event.featuredRobots.map((robot) => (
                    <li key={robot}>{robot}</li>
                  ))}
                </ul>
              </div>
            )}
            {event.technologyHighlights?.length > 0 && (
              <div>
                <p className="font-black text-neutral-950">Technology</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {event.technologyHighlights.map((topic) => (
                    <span key={topic} className="data-badge">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {planningItems.length > 0 && (
              <div>
                <p className="font-black text-neutral-950">Planning</p>
                <dl className="mt-2 grid gap-2">
                  {planningItems.map((item) => (
                    <div key={item.label} className="grid gap-0.5 sm:grid-cols-[10rem_1fr] sm:gap-3">
                      <dt className="font-bold text-neutral-600">{item.label}</dt>
                      <dd>{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {event.officialLinks?.length > 0 && (
              <div>
                <p className="font-black text-neutral-950">Official sources</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {officialLinks.map((item) => (
                    <a
                      key={`${item.label}-${item.url}`}
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className="focus-ring rounded-full border border-neutral-300 bg-white px-3 py-1.5 text-xs font-black text-blue-800 hover:border-blue-300"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </details>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-200 pt-4">
        <p className="text-xs font-semibold text-neutral-500">
          {event.lastChecked
            ? `Checked ${event.lastChecked}`
            : event.source || "Archived event"}
        </p>
        <div className="flex flex-wrap gap-2">
          {event.articlePath && (
            <Link
              to={event.articlePath}
              className="rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-black text-blue-800 transition hover:border-blue-400 hover:text-neutral-950"
            >
              Schedule &amp; coverage
            </Link>
          )}
          {event.website ? (
            <a
              href={event.website}
              target="_blank"
              rel="noreferrer"
              className="btn-primary px-4 py-2 text-xs sm:px-4 sm:py-2"
            >
              Official website
            </a>
          ) : !event.articlePath ? (
          <span className="text-xs font-bold text-neutral-500">
            Archived record
          </span>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <label className="grid gap-2 text-sm font-bold text-neutral-800">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="focus-ring min-h-11 rounded-xl border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-950 shadow-sm"
      >
        {options.map((option) => {
          const optionValue = Array.isArray(option) ? option[0] : option;
          const optionLabel = Array.isArray(option) ? option[1] : option;
          return (
            <option key={optionValue} value={optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </select>
    </label>
  );
}

function FilterPanel({
  category,
  countries,
  country,
  region,
  format,
  month,
  onChange,
  onReset,
  status,
  year,
  years,
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <FilterSelect
        label="Category"
        value={category}
        options={[filterDefaults.category, ...eventCategories]}
        onChange={(value) =>
          onChange("category", value, filterDefaults.category)
        }
      />
      <FilterSelect
        label="Region"
        value={region}
        options={[filterDefaults.region, ...regionOptions]}
        onChange={(value) => onChange("region", value, filterDefaults.region)}
      />
      <FilterSelect
        label="Country"
        value={country}
        options={[filterDefaults.country, ...countries]}
        onChange={(value) => onChange("country", value, filterDefaults.country)}
      />
      <FilterSelect
        label="Month"
        value={month}
        options={[
          [filterDefaults.month, filterDefaults.month],
          ...monthOptions,
        ]}
        onChange={(value) => onChange("month", value, filterDefaults.month)}
      />
      <FilterSelect
        label="Year"
        value={year}
        options={[filterDefaults.year, ...years]}
        onChange={(value) => onChange("year", value, filterDefaults.year)}
      />
      <FilterSelect
        label="Format"
        value={format}
        options={[
          filterDefaults.format,
          "In Person",
          "Online",
          "Hybrid",
          "Format not stated",
        ]}
        onChange={(value) => onChange("format", value, filterDefaults.format)}
      />
      <FilterSelect
        label="Status"
        value={status}
        options={[
          filterDefaults.status,
          "Upcoming",
          "Dates to be confirmed",
          "Happening Now",
          "Registration Open",
          "Registration Closed",
          "Finished",
          "Cancelled",
          "Postponed",
        ]}
        onChange={(value) => onChange("status", value, filterDefaults.status)}
      />
      <div className="sm:col-span-2 xl:col-span-4">
        <button
          type="button"
          onClick={onReset}
          className="btn-secondary w-full sm:w-auto px-4 py-2 text-xs sm:px-4 sm:py-2"
        >
          Reset filters
        </button>
      </div>
    </div>
  );
}

function CalendarEventLink({ event }) {
  const content = (
    <>
      <span className="block truncate font-bold">{event.name}</span>
      <span className="mt-0.5 block truncate text-[11px] opacity-75">
        {event.city || event.country || "Location TBC"}
      </span>
    </>
  );

  if (!event.website) {
    return (
      <div className="rounded-lg bg-neutral-100 px-2 py-1.5 text-xs text-neutral-700">
        {content}
      </div>
    );
  }

  return (
    <a
      href={event.website}
      target="_blank"
      rel="noreferrer"
      className="focus-ring block rounded-lg bg-blue-50 px-2 py-1.5 text-xs text-blue-900 transition hover:bg-blue-100"
    >
      {content}
    </a>
  );
}

function CalendarView({ events }) {
  const months = useMemo(() => buildCalendarMonths(events), [events]);
  const undated = events.filter((event) => !parseLocalDate(event.dateStart));

  if (!months.length && !undated.length) return null;

  return (
    <div className="grid gap-6">
      {months.map((calendarMonth) => {
        const firstDay = new Date(
          calendarMonth.year,
          calendarMonth.month - 1,
          1,
        ).getDay();
        const mondayOffset = (firstDay + 6) % 7;
        const daysInMonth = new Date(
          calendarMonth.year,
          calendarMonth.month,
          0,
        ).getDate();
        const eventsByDay = calendarMonth.events.reduce((result, event) => {
          const day = parseLocalDate(event.dateStart)?.getDate();
          if (!day) return result;
          if (!result[day]) result[day] = [];
          result[day].push(event);
          return result;
        }, {});

        return (
          <section key={calendarMonth.key} className="soft-card p-4 sm:p-6">
            <h2 className="text-2xl font-black tracking-tight text-neutral-950">
              {calendarMonth.label}
            </h2>

            <div className="mt-4 grid gap-3 md:hidden">
              {calendarMonth.events.map((event) => (
                <div
                  key={event.id}
                  className="grid grid-cols-[3.5rem_1fr] gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-3"
                >
                  <div className="text-center">
                    <span className="block text-xl font-black text-neutral-950">
                      {parseLocalDate(event.dateStart)?.getDate()}
                    </span>
                    <span className="text-[10px] font-black uppercase tracking-[0.14em] text-neutral-500">
                      {new Intl.DateTimeFormat("en", {
                        weekday: "short",
                      }).format(parseLocalDate(event.dateStart))}
                    </span>
                  </div>
                  <CalendarEventLink event={event} />
                </div>
              ))}
            </div>

            <div className="mt-5 hidden md:block">
              <div className="grid grid-cols-7 gap-2 text-center text-xs font-black uppercase tracking-[0.14em] text-neutral-500">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                  (day) => (
                    <div key={day}>{day}</div>
                  ),
                )}
              </div>
              <div className="mt-2 grid grid-cols-7 gap-2">
                {Array.from({ length: mondayOffset }).map((_, index) => (
                  <div
                    key={`blank-${index}`}
                    aria-hidden="true"
                    className="min-h-28 rounded-xl bg-neutral-50/60"
                  />
                ))}
                {Array.from({ length: daysInMonth }).map((_, index) => {
                  const day = index + 1;
                  return (
                    <div
                      key={day}
                      className="min-h-28 rounded-xl border border-neutral-200 bg-white p-2"
                    >
                      <span className="text-xs font-black text-neutral-500">
                        {day}
                      </span>
                      <div className="mt-2 grid gap-1.5">
                        {(eventsByDay[day] || []).map((event) => (
                          <CalendarEventLink key={event.id} event={event} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {undated.length > 0 && (
        <section className="soft-card p-4 sm:p-6">
          <h2 className="text-2xl font-black tracking-tight text-neutral-950">
            Date to be confirmed
          </h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {undated.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default function RoboticsEvents() {
  const { section } = useParams();
  const [urlSearchParams, setSearchParams] = useSearchParams();
  const [filtersReady, setFiltersReady] = useState(false);
  // Static HTML uses the unfiltered URL. Apply saved filters after hydration.
  const searchParams = useMemo(
    () => filtersReady ? urlSearchParams : new URLSearchParams(),
    [filtersReady, urlSearchParams],
  );
  const { subpage, events: scopedEvents } = getEventsBySubpage(section);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [communityEvents, setCommunityEvents] = useState([]);

  useEffect(() => {
    setFiltersReady(true);
    let active = true;
    if (typeof window !== "undefined" && window.__TECHNIA_DISABLE_REMOTE_DATA__) {
      return () => { active = false; };
    }
    import("../lib/firestoreCommunity")
      .then(({ getPublishedCommunityEvents }) => getPublishedCommunityEvents(100))
      .then((events) => { if (active) setCommunityEvents(events); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const focus = searchParams.get("focus") || "all";
  const searchTerm = searchParams.get("q") || "";
  const category = searchParams.get("category") || filterDefaults.category;
  const country = searchParams.get("country") || filterDefaults.country;
  const region = searchParams.get("region") || filterDefaults.region;
  const month = searchParams.get("month") || filterDefaults.month;
  const year = searchParams.get("year") || filterDefaults.year;
  const format = searchParams.get("format") || filterDefaults.format;
  const status = searchParams.get("status") || filterDefaults.status;
  const view = searchParams.get("view") === "calendar" ? "calendar" : "list";

  const sortedScopedEvents = useMemo(() => {
    const approved = subpage
      ? communityEvents.filter((event) => eventMatchesSubpage(event, subpage))
      : communityEvents;
    return sortEvents(dedupeEvents([...scopedEvents, ...approved]));
  }, [communityEvents, scopedEvents, subpage]);
  const activeEvents = useMemo(
    () => sortedScopedEvents.filter((event) => !isPastEvent(event)),
    [sortedScopedEvents],
  );
  const completedScopedEvents = useMemo(
    () => sortedScopedEvents.filter(isPastEvent).map(archiveFromEvent),
    [sortedScopedEvents],
  );
  const archiveEvents = useMemo(() => {
    const normalizedGlobalArchive = pastRoboticsEvents.map(
      normalizeArchiveEvent,
    );
    const combined = subpage
      ? [
          ...completedScopedEvents,
          ...normalizedGlobalArchive.filter((event) =>
            eventMatchesSubpage(event, subpage),
          ),
        ]
      : [...completedScopedEvents, ...normalizedGlobalArchive];
    return dedupeEvents(sortEvents(combined, true));
  }, [completedScopedEvents, subpage]);

  const showArchive = focus === "past" || status === "Finished";
  const sourceEvents = showArchive ? archiveEvents : activeEvents;

  const countries = useMemo(
    () =>
      Array.from(
        new Set(
          [...activeEvents, ...archiveEvents]
            .map((event) => event.country)
            .filter(Boolean),
        ),
      ).sort(),
    [activeEvents, archiveEvents],
  );
  const years = useMemo(
    () =>
      Array.from(
        new Set(
          [...activeEvents, ...archiveEvents].map(getEventYear).filter(Boolean),
        ),
      ).sort(),
    [activeEvents, archiveEvents],
  );

  const updateParam = (key, value, defaultValue = "") => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    const next = new URLSearchParams(searchParams);
    ["category", "country", "region", "month", "year", "format", "status"].forEach(
      (key) => next.delete(key),
    );
    setSearchParams(next, { replace: true });
  };

  const resetDiscovery = () => {
    const next = new URLSearchParams(searchParams);
    managedQueryKeys.forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  };

  const chooseFocus = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("focus");
    else next.set("focus", value);
    next.delete("category");
    next.delete("format");
    next.delete("status");
    setSearchParams(next, { replace: true });
  };

  const filteredEvents = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return sortEvents(
      sourceEvents.filter((event) => {
        const matchesSearch =
          !normalizedSearch ||
          searchableEventText(event).includes(normalizedSearch);
        const matchesCategory =
          category === filterDefaults.category || event.category === category;
        const matchesCountry =
          country === filterDefaults.country || event.country === country;
        const matchesMonth =
          month === filterDefaults.month ||
          getEventMonth(event) === month;
        const matchesYear =
          year === filterDefaults.year || getEventYear(event) === year;
        const matchesFormat =
          format === filterDefaults.format || getEventFormat(event) === format;
        const matchesStatus =
          status === filterDefaults.status ||
          getTemporalStatus(event) === status;
        return (
          matchesSearch &&
          matchesFocus(event, focus) &&
          matchesCategory &&
          matchesCountry &&
          matchesRegion(event, region) &&
          matchesMonth &&
          matchesYear &&
          matchesFormat &&
          matchesStatus
        );
      }),
      showArchive,
    );
  }, [
    category,
    country,
    region,
    focus,
    format,
    month,
    searchTerm,
    showArchive,
    sourceEvents,
    status,
    year,
  ]);

  const filterSignature = searchParams.toString();
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filterSignature, section]);

  const displayedEvents = filteredEvents.slice(0, visibleCount);
  const eventGroups = useMemo(
    () => groupEvents(displayedEvents, showArchive),
    [displayedEvents, showArchive],
  );
  const latestVerified = useMemo(() => {
    const checkedDates = scopedEvents
      .map((event) => event.lastChecked)
      .filter(Boolean)
      .map((value) => ({ value, time: Date.parse(value) }))
      .filter((item) => Number.isFinite(item.time))
      .sort((left, right) => right.time - left.time);
    return checkedDates[0]?.value || null;
  }, [scopedEvents]);

  const pagePath = subpage ? `/robotics-events/${section}` : "/robotics-events";
  const pageTitle = subpage
    ? `${subpage.title} TechniaHQRobot Events`
    : "Robotics Events and Conferences 2026 and 2027";
  const pageDescription = subpage
    ? subpage.description
    : "Find humanoid robotics, Physical AI and robotics events worldwide through December 2027, with organizer sources and clear date status.";
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Robotics Events", to: "/robotics-events" },
    ...(subpage ? [{ label: subpage.title, to: pagePath }] : []),
  ];

  const schema = [
    buildBreadcrumbSchema(breadcrumbs),
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: subpage ? subpage.title : "Robotics Events Guide",
      description: pageDescription,
      url: `${seoConfig.baseUrl}${pagePath}`,
      dateModified: "2026-10-06",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: displayedEvents.map((event, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: event.name,
          url: event.website,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: eventFaqs.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  ];

  const activeFilterCount = [
    category !== filterDefaults.category,
    country !== filterDefaults.country,
    region !== filterDefaults.region,
    month !== filterDefaults.month,
    year !== filterDefaults.year,
    format !== filterDefaults.format,
    status !== filterDefaults.status,
  ].filter(Boolean).length;

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        path={pagePath}
        type="article"
        schema={schema}
        modifiedTime="2026-10-06"
      />
      <Breadcrumbs items={breadcrumbs} />

      <header className="border-b border-neutral-200 bg-gradient-to-b from-blue-50 via-white to-white">
        <div className="site-shell py-10 sm:py-14 lg:py-16">
          <div className="max-w-4xl">
            <span className="eyebrow-chip">Robotics Events Guide</span>
            <h1 className="mt-5 text-4xl font-black tracking-tight text-neutral-950 sm:text-5xl lg:text-6xl">
              {subpage ? subpage.title : "Robotics Events 2026 and 2027"}
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-8 text-neutral-600 sm:text-lg">
              {subpage
                ? subpage.description
                : "Find humanoid robotics and Physical AI events through December 2027 in Europe, China, Japan, the United States, the UAE and other Asian markets. Research conferences, exhibitions and competitions are listed with organizer sources."}
            </p>
          </div>

          <div className="mt-7 flex flex-wrap gap-2 text-sm font-bold text-neutral-700">
            <span className="data-badge">
              {activeEvents.length} upcoming events
            </span>
            <span className="data-badge">
              {archiveEvents.length} archived events
            </span>
            {latestVerified && (
              <span className="data-badge">Updated {latestVerified}</span>
            )}
          </div>

          <p className="mt-4 max-w-3xl text-sm leading-7 text-neutral-600">
            Dates marked to be confirmed reflect an incomplete announcement or
            conflicting organizer pages. These events remain searchable by year
            and announced month, without being assigned a day in the calendar.
          </p>

          <div className="mt-8 max-w-4xl">
            <label htmlFor="event-search" className="sr-only">
              Search robotics events
            </label>
            <div className="flex flex-col gap-3 rounded-2xl border border-neutral-200 bg-white p-3 shadow-[0_18px_60px_rgba(15,23,42,0.08)] sm:flex-row">
              <div className="relative min-w-0 flex-1">
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 fill-none stroke-neutral-500 stroke-2"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
                <input
                  id="event-search"
                  type="search"
                  value={searchTerm}
                  onChange={(event) => updateParam("q", event.target.value)}
                  placeholder="Search event, city, country, company, university or technology"
                  className="focus-ring min-h-12 w-full rounded-xl border border-neutral-300 bg-white py-3 pl-12 pr-4 text-base text-neutral-950 placeholder:text-neutral-400"
                />
              </div>
              {searchTerm ? (
                <button
                  type="button"
                  onClick={() => updateParam("q", "")}
                  className="btn-secondary whitespace-nowrap"
                >
                  Clear search
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setSubmitOpen(true)}
                  className="btn-primary whitespace-nowrap"
                >
                  Submit an event
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div>
        <section
          className="site-shell py-7 sm:py-9"
          aria-labelledby="event-discovery-title"
        >
          <div className="soft-card p-4 sm:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
                  Find an event
                </p>
                <h2
                  id="event-discovery-title"
                  className="mt-1 text-2xl font-black tracking-tight text-neutral-950"
                >
                  Filter the calendar in seconds
                </h2>
              </div>
              <div
                className="inline-flex w-full rounded-xl border border-neutral-200 bg-neutral-50 p-1 sm:w-auto"
                aria-label="Event view"
              >
                <button
                  type="button"
                  onClick={() => updateParam("view", "list", "list")}
                  aria-pressed={view === "list"}
                  className={`focus-ring flex-1 rounded-lg px-4 py-2 text-sm font-black transition sm:flex-none ${view === "list" ? "bg-white text-neutral-950 shadow-sm" : "text-neutral-500 hover:text-neutral-950"}`}
                >
                  List View
                </button>
                <button
                  type="button"
                  onClick={() => updateParam("view", "calendar", "list")}
                  aria-pressed={view === "calendar"}
                  className={`focus-ring flex-1 rounded-lg px-4 py-2 text-sm font-black transition sm:flex-none ${view === "calendar" ? "bg-white text-neutral-950 shadow-sm" : "text-neutral-500 hover:text-neutral-950"}`}
                >
                  Calendar View
                </button>
              </div>
            </div>

            <div
              className="mt-5 flex flex-wrap gap-2"
              aria-label="Quick event filters"
            >
              {quickFilters.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => chooseFocus(item.value)}
                  aria-pressed={
                    focus === item.value ||
                    (item.value === "all" && focus === "all")
                  }
                  className={`focus-ring shrink-0 rounded-full border px-3.5 py-2 text-xs font-black transition ${focus === item.value || (item.value === "all" && focus === "all") ? "border-blue-700 bg-blue-600 text-white" : "border-neutral-200 bg-white text-neutral-700 hover:border-blue-200 hover:text-blue-700"}`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <details className="mt-5 rounded-xl border border-neutral-200 bg-neutral-50 p-4 md:hidden">
              <summary className="focus-ring cursor-pointer rounded-lg text-sm font-black text-neutral-950">
                Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
              </summary>
              <div className="mt-4">
                <FilterPanel
                  category={category}
                  countries={countries}
                  country={country}
                  region={region}
                  format={format}
                  month={month}
                  onChange={updateParam}
                  onReset={resetFilters}
                  status={status}
                  year={year}
                  years={years}
                />
              </div>
            </details>

            <div className="mt-5 hidden rounded-xl border border-neutral-200 bg-neutral-50 p-4 md:block">
              <FilterPanel
                category={category}
                countries={countries}
                country={country}
                region={region}
                format={format}
                month={month}
                onChange={updateParam}
                onReset={resetFilters}
                status={status}
                year={year}
                years={years}
              />
            </div>
          </div>
        </section>

        <section
          id="upcoming-events"
          className="site-shell pb-10 sm:pb-14"
          aria-labelledby="event-results-title"
        >
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-neutral-500">
                {showArchive ? "Archive" : "Upcoming calendar"}
              </p>
              <h2
                id="event-results-title"
                className="mt-1 text-3xl font-black tracking-tight text-neutral-950"
              >
                {filteredEvents.length}{" "}
                {filteredEvents.length === 1 ? "event" : "events"} found
              </h2>
            </div>
            <p className="text-sm text-neutral-500" aria-live="polite">
              Showing {Math.min(displayedEvents.length, filteredEvents.length)}{" "}
              of {filteredEvents.length}
            </p>
          </div>

          {filteredEvents.length === 0 ? (
            <div className="soft-card p-8 text-center sm:p-12">
              <h3 className="text-2xl font-black text-neutral-950">
                No event matches these filters
              </h3>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-neutral-600">
                Clear the search or reset the region, country, date, format and status
                filters to broaden the calendar.
              </p>
              <button
                type="button"
                onClick={resetDiscovery}
                className="btn-primary mt-5"
              >
                Reset event search
              </button>
            </div>
          ) : view === "calendar" ? (
            <CalendarView events={displayedEvents} />
          ) : (
            <div className="grid gap-8">
              {eventGroups.map(([groupName, events]) => (
                <section
                  key={groupName}
                  aria-labelledby={`group-${groupName.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="mb-4 flex items-center gap-3">
                    <h3
                      id={`group-${groupName.replace(/\s+/g, "-").toLowerCase()}`}
                      className="text-2xl font-black tracking-tight text-neutral-950"
                    >
                      {groupName}
                    </h3>
                    <span className="data-badge">{events.length}</span>
                  </div>
                  <div className="grid gap-4 lg:grid-cols-2">
                    {events.map((event) => (
                      <EventCard key={event.id} event={event} headingLevel={4} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          {visibleCount < filteredEvents.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                className="btn-secondary min-w-44"
              >
                Load More
              </button>
            </div>
          )}
        </section>

        {!showArchive && archiveEvents.length > 0 && (
          <section className="site-shell py-8 sm:py-10" id="past-events">
            <details className="soft-card p-5 sm:p-7">
              <summary className="focus-ring cursor-pointer rounded-xl">
                <span className="block text-xs font-black uppercase tracking-[0.18em] text-neutral-500">
                  Past Events
                </span>
                <span className="mt-1 block text-2xl font-black tracking-tight text-neutral-950">
                  Open the robotics events archive ({archiveEvents.length})
                </span>
              </summary>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-neutral-600">
                Past events remain available as an archive.
                They are kept separate from active registration opportunities.
              </p>
              <div className="mt-6 grid gap-4 lg:grid-cols-2">
                {archiveEvents.slice(0, 12).map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
              {archiveEvents.length > 12 && (
                <button
                  type="button"
                  onClick={() => chooseFocus("past")}
                  className="btn-secondary mt-6"
                >
                  Browse all past events
                </button>
              )}
            </details>
          </section>
        )}

        <section className="site-shell py-10 sm:py-14">
          <SectionIntro
            eyebrow="Event guides"
            title="Browse robotics events by purpose"
            description="Browse conferences, exhibitions, humanoid competitions and industrial automation events."
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {roboticsEventSubpages.map((item) => (
              <DeepCard
                key={item.slug}
                title={item.title}
                description={item.description}
                to={`/robotics-events/${item.slug}`}
                label="Open event guide"
              />
            ))}
          </div>
        </section>

        <section className="site-shell py-10 sm:py-14">
          <details className="soft-card p-5 sm:p-7">
            <summary className="focus-ring cursor-pointer rounded-xl">
              <span className="block text-xs font-black uppercase tracking-[0.18em] text-neutral-500">
                Planning resources
              </span>
              <span className="mt-1 block text-2xl font-black tracking-tight text-neutral-950">
                Choose events by robot category and region
              </span>
            </summary>
            <div className="mt-7 grid gap-6">
              <div>
                <h2 className="text-2xl font-black tracking-tight text-neutral-950">
                  Events by robotics category
                </h2>
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  {eventCategoryGuides.map((guide) => (
                    <DeepCard
                      key={guide.category}
                      title={guide.category}
                      description={guide.summary}
                      to={guide.internalLink}
                      label="Open related robotics guide"
                    >
                      <div className="mt-4">
                        <p className="mb-2 text-sm font-black text-neutral-950">
                          Events to watch
                        </p>
                        <PillList items={guide.bestEvents} />
                      </div>
                    </DeepCard>
                  ))}
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-black tracking-tight text-neutral-950">
                  Regional robotics event map
                </h2>
                <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {regionGuides.map((guide) => (
                    <article
                      key={guide.region}
                      className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5"
                    >
                      <h3 className="text-xl font-black tracking-tight text-neutral-950">
                        {guide.region}
                      </h3>
                      <p className="mt-3 text-sm leading-7 text-neutral-600">
                        {guide.summary}
                      </p>
                      <div className="mt-4">
                        <PillList items={guide.watchFor} />
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </details>
        </section>

        <section className="site-shell py-10 sm:py-14">
          <div className="soft-card grid gap-6 bg-neutral-50 p-6 lg:grid-cols-[1fr_auto] lg:items-center sm:p-8">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
                Submit an event
              </p>
              <h2 className="mt-2 text-3xl font-black tracking-tight text-neutral-950">
                Add a robotics event to TechniaHQRobot
              </h2>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-neutral-600">
                Send the official event page with its name, organizer, city,
                country, dates, category, registration status and public ticket
                information. The listing should use verified source data only.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSubmitOpen(true)}
              className="btn-primary whitespace-nowrap"
            >
              Submit Event Details
            </button>
          </div>
        </section>

        <section className="site-shell py-10 sm:py-14">
          <SectionIntro
            eyebrow="Robotics map"
            title="Continue into the technical stack"
            description="Connect events with the robots, sensors, software, companies and Physical AI systems covered across TechniaHQRobot."
            action={<LinkPills items={internalLinks} />}
          />
        </section>

        <FAQBlock items={eventFaqs} />
        <CTASection
          title="Follow TechniaHQRobot for robotics event analysis"
          description="Get robotics event coverage across humanoids, AI robotics, industrial automation, robot components, ROS, drones, medical robotics, startups and global conferences."
        />
      </div>

      <EventSubmissionModal open={submitOpen} onClose={() => setSubmitOpen(false)} />

      <a
        href="#event-search"
        aria-label="Back to event search"
        className="focus-ring fixed bottom-5 right-5 z-30 hidden h-11 w-11 items-center justify-center rounded-full border border-neutral-200 bg-white text-lg font-black text-neutral-950 shadow-lg transition hover:-translate-y-0.5 hover:border-blue-200 hover:text-blue-700 sm:flex"
      >
        ↑
      </a>
    </>
  );
}
